/*
  card-list-controller.js — in-place behavior for the c-card-list listings
  (news / pro-staff), no full page navigation on any of the three interactions:

   - Pagination + type filter: intercept the listing's real <a> links (the pager
     parts and the type-filter dropdown options) and swap the section via
     Shopify's Section Rendering API (?section_id=...). Server-rendered, so the
     count line and pager stay exact and pagination composes with the active tag.
   - Search: query Shopify's Predictive Search API for matching ARTICLES (name +
     content) and render the result cards into the grid client-side — the same
     JSON-to-card pattern the PLP uses for Searchanise. Clearing the box restores
     the current server view.

  Links stay real <a href> and the search form still posts to native /search, so
  the whole thing degrades to normal navigation with JS off. Listeners are
  delegated on document so they survive the innerHTML swaps.
*/
(function () {
  'use strict';

  const SEARCH_DEBOUNCE = 250;

  function init() {
    const root = document.querySelector('.c-card-list');
    if (!root) return;

    const sectionEl = root.closest('[id^="shopify-section-"]');
    const sectionId = sectionEl ? sectionEl.id.replace('shopify-section-', '') : null;

    const tpl = readTemplate(root);
    const searchState = { timer: null, controller: null };

    // Pagination + filter links -> Section Rendering swap.
    document.addEventListener('click', function (event) {
      const link = event.target.closest('.c-card-list__pagination a[href], .c-card-list__filter-option[href]');
      if (!link || !root.contains(link)) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!sectionId) return; // no section id -> let the link navigate normally
      event.preventDefault();
      const details = link.closest('details');
      if (details) details.open = false;
      navigate(link.href, sectionId, root, { push: true, scroll: true });
    });

    // Type-filter dropdown: a native <details> only toggles via its own summary,
    // so an outside click leaves it open. Close it when the click lands outside
    // the open dropdown. Clicks inside (summary toggle, option select) are left
    // to the handlers above.
    document.addEventListener('click', function (event) {
      const openFilter = root.querySelector('.c-card-list__filter[open]');
      if (!openFilter || openFilter.contains(event.target)) return;
      openFilter.open = false;
    });

    window.addEventListener('popstate', function () {
      if (sectionId) navigate(location.href, sectionId, root, { push: false, scroll: false });
    });

    // Search: keep Enter from leaving to /search; live-query predictive search.
    document.addEventListener('submit', function (event) {
      const form = event.target.closest('.c-card-list__search');
      if (form && root.contains(form)) event.preventDefault();
    });

    document.addEventListener('input', function (event) {
      const input = event.target.closest('.c-card-list__search-input');
      if (!input || !root.contains(input)) return;
      const form = input.closest('.c-card-list__search');
      const mode = (form && form.dataset.searchMode) || 'predictive';
      const scope = (form && form.dataset.searchScope) || '';
      const q = input.value.trim();
      window.clearTimeout(searchState.timer);
      searchState.timer = window.setTimeout(function () {
        if (mode === 'client') {
          clientFilter(q, root, sectionId, tpl);
        } else {
          runSearch(q, root, sectionId, tpl, searchState, scope);
        }
      }, SEARCH_DEBOUNCE);
    });
  }

  // ---- Section Rendering swap (pagination + filter) ----
  function navigate(href, sectionId, root, opts) {
    const fetchUrl = new URL(href, location.href);
    fetchUrl.searchParams.set('section_id', sectionId);
    root.setAttribute('aria-busy', 'true');

    fetch(fetchUrl.toString())
      .then(function (r) { if (!r.ok) throw new Error('section ' + r.status); return r.text(); })
      .then(function (html) {
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.c-card-list');
        if (!fresh) throw new Error('no .c-card-list in response');
        preReveal(fresh); // mount cards already-revealed: the swap is instant, not a re-run of the scroll entrance
        root.innerHTML = fresh.innerHTML;
        root.removeAttribute('aria-busy');
        if (opts.push) window.history.pushState({}, '', href);
        if (opts.scroll) scrollToListTop(root);
      })
      .catch(function () { window.location.assign(href); });
  }

  // ---- Predictive search (client-rendered cards) ----
  function runSearch(q, root, sectionId, tpl, state, scope) {
    const grid = root.querySelector('.c-card-list__grid');
    if (!grid) return;

    if (q === '') {
      // Restore the current server view (respects the active type filter).
      if (sectionId) navigate(location.href, sectionId, root, { push: false, scroll: false });
      return;
    }

    if (state.controller) state.controller.abort();
    state.controller = new AbortController();

    const url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=article&resources[limit]=10';
    root.setAttribute('aria-busy', 'true');

    fetch(url, { signal: state.controller.signal })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        const all = (data.resources && data.resources.results && data.resources.results.articles) || [];
        // Scope to this blog (predictive search spans all blogs); no scope -> all.
        const articles = scope
          ? all.filter(function (a) { return a.url && a.url.indexOf(scope) !== -1; })
          : all;

        const pager = root.querySelector('.c-card-list__pagination');
        const count = root.querySelector('.c-card-list__count');

        grid.innerHTML = articles.length
          ? articles.map(function (a) { return cardHtml(a, tpl); }).join('')
          : '<p class="c-card-list__empty">' + esc(tpl.noResults) + '</p>';
        if (pager) pager.innerHTML = '';
        if (count) count.textContent = tpl.resultsTemplate.replace('__N__', String(articles.length));

        preReveal(root); // freshly built result cards mount already-revealed (no entrance re-run)
        root.removeAttribute('aria-busy');
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        root.removeAttribute('aria-busy');
      });
  }

  // ---- Client-side name filter (rendered cards stay put; no API) ----
  // For listings whose cards can't be reproduced from search JSON (e.g. the
  // pro-staff trading cards). Matches the query against each card's name
  // (.c-card__title, else the card's aria-label) and shows/hides in place.
  // Empty query re-fetches the authoritative server view (cards + pager + count).
  function clientFilter(q, root, sectionId, tpl) {
    const grid = root.querySelector('.c-card-list__grid');
    if (!grid) return;
    const query = q.toLowerCase();

    if (query === '') {
      if (sectionId) navigate(location.href, sectionId, root, { push: false, scroll: false });
      return;
    }

    let matches = 0;
    Array.prototype.forEach.call(grid.children, function (card) {
      const titleEl = card.querySelector('.c-card__title');
      const name = ((titleEl && titleEl.textContent) || card.getAttribute('aria-label') || card.textContent || '').toLowerCase();
      const show = name.indexOf(query) !== -1;
      // inline display, not the hidden attribute: .c-card sets display:flex/block,
      // which (author CSS) overrides the UA [hidden]{display:none} rule.
      card.style.display = show ? '' : 'none';
      if (show) {
        card.setAttribute('data-revealed', 'true');
        matches += 1;
      }
    });

    const pager = root.querySelector('.c-card-list__pagination');
    if (pager) pager.hidden = true; // pager is page-scoped; irrelevant while filtering in place
    const count = root.querySelector('.c-card-list__count');
    if (count) count.textContent = tpl.resultsTemplate.replace('__N__', String(matches));
  }

  function cardHtml(a, tpl) {
    const media = a.image
      ? '<div class="c-card__media"><img src="' + esc(sizeImg(a.image)) + '" alt="' + esc(a.title) + '" loading="lazy">' +
        (a.tags && a.tags.length ? '<span class="c-card__badge">' + esc(a.tags[0]) + '</span>' : '') +
        '</div>'
      : '';
    const date = a.published_at ? formatDate(a.published_at) : '';
    const excerpt = a.summary_html ? stripHtml(a.summary_html) : '';

    return '<article class="c-card">' + media +
      '<div class="c-card__body">' +
        '<p class="c-card__meta"><span class="c-card__meta-icon">' + tpl.calIcon + '</span><span>' + esc(date) + '</span></p>' +
        '<h3 class="c-card__title">' + esc(a.title) + '</h3>' +
        (excerpt ? '<p class="c-card__excerpt">' + esc(excerpt) + '</p>' : '') +
        '<div class="c-card__footer"><a class="c-card__read-more" href="' + esc(a.url) + '">' + esc(tpl.readMore) +
          '<span class="c-card__read-more-icon">' + tpl.arrowIcon + '</span></a></div>' +
      '</div>' +
    '</article>';
  }

  // ---- helpers ----
  function readTemplate(root) {
    const form = root.querySelector('.c-card-list__search');
    const calEl = root.querySelector('.c-card__meta-icon');
    const arrowEl = root.querySelector('.c-card__read-more-icon');
    return {
      calIcon: calEl ? calEl.innerHTML : '',
      arrowIcon: arrowEl ? arrowEl.innerHTML : '',
      readMore: (form && form.dataset.readMoreLabel) || 'Read more',
      resultsTemplate: (form && form.dataset.resultsTemplate) || '__N__ results',
      noResults: (form && form.dataset.noResultsLabel) || 'No articles found'
    };
  }

  function sizeImg(url) {
    return url + (url.indexOf('?') !== -1 ? '&' : '?') + 'width=720';
  }

  function formatDate(iso) {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
  }

  function stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = (tmp.textContent || '').trim();
    return text.length > 160 ? text.slice(0, 160).trim() + '…' : text;
  }

  function esc(s) {
    return String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function scrollToListTop(root) {
    const header = document.querySelector('.c-header');
    const offset = (header ? header.offsetHeight : 0) + 16;
    const top = root.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    if (!header) return;
    const show = function () { header.dataset.hidden = 'false'; };
    if ('onscrollend' in window) window.addEventListener('scrollend', show, { once: true });
    window.setTimeout(show, 700);
  }

  // Cards swapped in by the controller (filter / pagination / search) are already
  // on-screen, so they mount in their final revealed state rather than re-running
  // the scroll-entrance fade (which flashed/blinked on every swap). The initial
  // page-load entrance is still handled by reveal.js's own observer.
  function preReveal(scope) {
    const grid = scope.querySelector('[data-reveal]');
    if (!grid) return;
    grid.querySelectorAll(':scope > *').forEach(function (item) {
      item.setAttribute('data-revealed', 'true');
    });
  }

  document.addEventListener('DOMContentLoaded', init);
})();
